import type { ChangeEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { toastManager } from '@systemeio/components'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import ConfirmationModal from 'shared/components/confirmation-modal'
import type { PaginationData } from 'shared/components/pagination'
import { ConflictError } from 'shared/errors/conflict-error'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import useUser, { isWorkspaceMember } from 'shared/hooks/use-user'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { useCancelInvitation } from 'modules/community/api/cancel-invitation'
import { useBannedMembers } from 'modules/community/api/get-banned-members'
import { useInvitations } from 'modules/community/api/get-invitations'
import { useMembers } from 'modules/community/api/get-members'
import { communityKeys } from 'modules/community/api/key-factory'
import * as postsApi from 'modules/community/api/posts-api'
import { useResendInvitation } from 'modules/community/api/resend-invitation'
import { ACTIVITY_CLEANUP_PERIOD_DURATION } from 'modules/community/const/post-consts'
import { BanPeriodUnitEnum } from 'modules/community/enums/member-enum'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { BannedTabContent } from 'modules/community/members/components/banned-tab-content'
import { InvitationsSection } from 'modules/community/members/components/invitations-section'
import { MembersTabContent } from 'modules/community/members/components/members-tab-content'
import { MembersTabs, TABS, type TabKey } from 'modules/community/members/components/members-tabs'
import { RemoveRecentActivitiesCheckbox } from 'modules/community/post/remove-recent-activities-checkbox'
import type { InvitationInterface } from 'modules/community/types/invitation-interface'
import type { MemberInterface } from 'modules/community/types/member-interface'

function MembersPage(): ReactNode {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { user } = useUser()
  const { tab } = router.query
  const communityPath = router.query.communityPath ?? ''

  const [pageIndex, setPageIndex] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300)
  const [removableMemberId, setRemovableMemberId] = useState<number | null>(null)
  const [banMemberId, setBanMemberId] = useState<{ userId: number; displayName: string } | null>(
    null,
  )
  const [unbanId, setUnbanId] = useState<number | null>(null)
  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [cancelInvitationId, setCancelInvitationId] = useState<number | null>(null)

  const { data: community } = useCommunityWithTopics()

  const isUserAllowedToInvite: boolean | undefined =
    user === undefined || community === undefined ? undefined : isWorkspaceMember(user)

  const adminQueriesEnabled = Boolean(communityPath) && isUserAllowedToInvite === true

  const { data: membersCountData } = useMembers({
    communityPath,
    page: 1,
    searchBy: '',
  })
  const { data: bannedCountData } = useBannedMembers({
    communityPath,
    page: 1,
    searchBy: '',
    enabled: adminQueriesEnabled,
  })
  const { data: pendingData } = useInvitations({
    communityPath,
    status: 'pending',
    page: 1,
    searchBy: '',
    enabled: adminQueriesEnabled,
  })
  const { data: expiredData } = useInvitations({
    communityPath,
    status: 'expired',
    page: 1,
    searchBy: '',
    enabled: adminQueriesEnabled,
  })

  const resendMutation = useResendInvitation({ communityPath })
  const cancelMutation = useCancelInvitation({ communityPath })

  // TODO: research how to handle tabs more correctly
  const activeTab: TabKey =
    isUserAllowedToInvite === true && TABS.includes(tab as TabKey) ? (tab as TabKey) : 'members'

  useEffect(() => {
    setPageIndex(1)
    setSearchQuery('')
  }, [activeTab])

  if (!communityPath) {
    return null
  }

  return (
    <div data-testid="community-route-community-members">
      <MembersTabs
        activeTab={activeTab}
        searchQuery={searchQuery}
        onSearchChange={(event: ChangeEvent<HTMLInputElement>): void => {
          setSearchQuery(event.target.value)
          setPageIndex(1)
        }}
        onClearSearch={() => {
          setSearchQuery('')
        }}
        isUserAllowedToInvite={Boolean(isUserAllowedToInvite)}
        membersCount={membersCountData?.total ?? 0}
        pendingCount={pendingData?.total ?? 0}
        expiredCount={expiredData?.total ?? 0}
        bannedCount={bannedCountData?.total ?? 0}
      />

      {activeTab === 'members' && (
        <MembersTabContent
          communityPath={communityPath}
          pageIndex={pageIndex}
          searchQuery={debouncedSearchQuery}
          setPageIndex={setPageIndex}
          setRemovableMemberId={setRemovableMemberId}
          setBanMemberId={setBanMemberId}
        />
      )}

      {activeTab === 'pending' && isUserAllowedToInvite && (
        <InvitationsSection
          communityPath={communityPath}
          status="pending"
          searchQuery={debouncedSearchQuery}
          page={pageIndex}
          onPageChange={setPageIndex}
          onResend={(invitation: InvitationInterface): void => {
            resendMutation.mutate(invitation.id, {
              onSuccess: () => {
                toastManager.add({ type: 'success', title: t('members.invitation.resent_toast') })
              },
              onError: (resendError: Error) => {
                if (resendError instanceof ConflictError) {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.conflict_error_toast'),
                  })
                  void queryClient.invalidateQueries({
                    queryKey: communityKeys.invitations.lists(),
                  })
                } else {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.resend_failed_toast'),
                  })
                }
              },
            })
          }}
          onCancel={(invitation: InvitationInterface): void => {
            setCancelInvitationId(invitation.id)
          }}
        />
      )}

      {activeTab === 'expired' && isUserAllowedToInvite && (
        <InvitationsSection
          communityPath={communityPath}
          status="expired"
          searchQuery={debouncedSearchQuery}
          page={pageIndex}
          onPageChange={setPageIndex}
          onReinvite={(invitation: InvitationInterface): void => {
            resendMutation.mutate(invitation.id, {
              onSuccess: () => {
                toastManager.add({
                  type: 'success',
                  title: t('members.invitation.reinvited_toast'),
                })
              },
              onError: (reinviteError: Error) => {
                if (reinviteError instanceof ConflictError) {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.conflict_error_toast'),
                  })
                  void queryClient.invalidateQueries({
                    queryKey: communityKeys.invitations.lists(),
                  })
                } else {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.resend_failed_toast'),
                  })
                }
              },
            })
          }}
        />
      )}

      {activeTab === 'banned' && isUserAllowedToInvite && (
        <BannedTabContent
          communityPath={communityPath}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          searchQuery={debouncedSearchQuery}
          unbanId={unbanId}
          setUnbanId={setUnbanId}
        />
      )}

      {!!removableMemberId && (
        <ConfirmationModal
          onConfirm={() => {
            if (!removableMemberId) return
            const id = removableMemberId
            postsApi
              .deleteUser(id, communityPath)
              .then(() => {
                queryClient.setQueriesData<PaginationData<MemberInterface[]>>(
                  { queryKey: communityKeys.members.lists() },
                  old => {
                    if (!old) return old
                    const data = old.data.filter(member => member.id !== id)
                    if (data.length === old.data.length) return old
                    return { total: Math.max(0, old.total - 1), data }
                  },
                )
                void queryClient.invalidateQueries({ queryKey: communityKeys.members.lists() })
                toastManager.add({ type: 'success', title: t('members.remove_success_toast') })
                setRemovableMemberId(null)
              })
              .catch(() => {
                toastManager.add({ type: 'error', title: t('members.remove_failed_toast') })
              })
          }}
          onCloseModal={() => {
            setRemovableMemberId(null)
          }}
          confirmationContent={t('settings.form.delete_member_confirmation.label')}
        />
      )}
      {!!banMemberId && (
        <ConfirmationModal
          onConfirm={() => {
            if (!banMemberId) return
            const id = banMemberId.userId
            postsApi
              .banUser(
                id,
                communityPath,
                removeRecentActivitiesChecked
                  ? {
                      deleteRecentActivityOptions: {
                        periodUnit: BanPeriodUnitEnum.day,
                        periodDuration: ACTIVITY_CLEANUP_PERIOD_DURATION,
                      },
                    }
                  : undefined,
              )
              .then(() => {
                queryClient.setQueriesData<PaginationData<MemberInterface[]>>(
                  { queryKey: communityKeys.members.lists() },
                  old => {
                    if (!old) return old
                    const data = old.data.filter(member => member.userId !== id)
                    if (data.length === old.data.length) return old
                    return { total: Math.max(0, old.total - 1), data }
                  },
                )
                void queryClient.invalidateQueries({ queryKey: communityKeys.members.lists() })
                void queryClient.invalidateQueries({
                  queryKey: communityKeys.bannedMembers.lists(),
                })
                setBanMemberId(null)
                toastManager.add({ type: 'success', title: t('members.ban_success_toast') })
              })
              .catch(() => {
                toastManager.add({ type: 'error', title: t('members.ban_failed_toast') })
              })
          }}
          onCloseModal={() => {
            setBanMemberId(null)
            setRemoveRecentActivitiesChecked(false)
          }}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.ban_user_confirmation.label')}
              memberDisplayName={banMemberId.displayName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => {
                setRemoveRecentActivitiesChecked(prev => !prev)
              }}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {!!cancelInvitationId && (
        <ConfirmationModal
          onConfirm={() => {
            if (!cancelInvitationId) return
            cancelMutation.mutate(cancelInvitationId, {
              onSuccess: () => {
                toastManager.add({ type: 'success', title: t('members.invitation.canceled_toast') })
                setCancelInvitationId(null)
                void queryClient.invalidateQueries({ queryKey: communityKeys.invitations.lists() })
              },
              onError: (cancelError: Error) => {
                if (cancelError instanceof ConflictError) {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.conflict_error_toast'),
                  })
                  void queryClient.invalidateQueries({
                    queryKey: communityKeys.invitations.lists(),
                  })
                } else {
                  toastManager.add({
                    type: 'error',
                    title: t('members.invitation.cancel_failed_toast'),
                  })
                }
              },
            })
          }}
          onCloseModal={() => {
            setCancelInvitationId(null)
          }}
          confirmationContent={
            <p className="text-base text-text-primary">
              {t('members.invitation.cancel_confirmation.body')}
            </p>
          }
        />
      )}
    </div>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default MembersPage
